// Authentication configuration for the application
// Centralized settings for session durations and security

export const authConfig = {
  // Session durations in milliseconds
  sessionDurations: {
    // Staff SMS authentication - reduced from 8 hours to 2 hours
    staff: 2 * 60 * 60 * 1000, // 2 hours
    
    // Admin SMS authentication - reduced from 8 hours to 4 hours  
    admin: 4 * 60 * 60 * 1000, // 4 hours
    
    // Location bypass - 1 hour for security
    locationBypass: 1 * 60 * 60 * 1000, // 1 hour
    
    // SMS verification codes - 10 minutes
    smsVerification: 10 * 60 * 1000, // 10 minutes
  },
  
  // Security settings
  security: {
    // Time before session expiry to show warning (in milliseconds)
    sessionWarningTime: 15 * 60 * 1000, // 15 minutes
    
    // Inactivity timeout (in milliseconds)
    inactivityTimeout: 30 * 60 * 1000, // 30 minutes
    
    // Maximum authentication attempts before temporary lockout
    maxAuthAttempts: 5,
    
    // Lockout duration in milliseconds
    lockoutDuration: 15 * 60 * 1000, // 15 minutes
  },
  
  // Debug mode - set to false in production
  debug: true,
};

// Helper functions for authentication
export const createAuthData = (type: 'staff' | 'admin', phoneNumber?: string, additionalData?: any) => {
  const now = new Date().getTime();
  const duration = authConfig.sessionDurations[type];
  
  return {
    authenticated: true,
    timestamp: now,
    expiry: now + duration,
    phoneNumber,
    role: type,
    isAdmin: type === 'admin',
    ...additionalData
  };
};

export const createLocationBypassData = () => {
  const now = new Date().getTime();
  
  return {
    authenticated: true,
    timestamp: now,
    expiry: now + authConfig.sessionDurations.locationBypass,
    type: 'location_bypass'
  };
};

export const isSessionExpired = (authData: any): boolean => {
  if (!authData || !authData.expiry) {
    return true;
  }
  
  return authData.expiry <= new Date().getTime();
};

export const getTimeUntilExpiry = (authData: any): number => {
  if (!authData || !authData.expiry) {
    return 0;
  }
  
  return Math.max(0, authData.expiry - new Date().getTime());
};

export const shouldShowSessionWarning = (authData: any): boolean => {
  const timeUntilExpiry = getTimeUntilExpiry(authData);
  return timeUntilExpiry > 0 && timeUntilExpiry <= authConfig.security.sessionWarningTime;
};

// Log authentication events for debugging
export const logAuthEvent = (event: string, data?: any) => {
  if (authConfig.debug) {
    console.log(`🔐 Auth Event: ${event}`, {
      timestamp: new Date().toISOString(),
      ...data
    });
  }
};